import {InfoTooltip, Switch} from '@wandb/components';
import React, {useId} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  calculateIsSettingEnabled,
  type SettingOverrideCounts,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {
  type ClearSettingRow,
  useClearSettingRows,
} from '../hooks/useClearSettingRows';
import {type ClearSettingFnType} from '../hooks/useSettingOverrides';
import {type LinePlotSettings, type SettingLevel} from '../types';
import {ClearSettingsRows} from './ClearSettingsRow';
import {ResetSettingButton} from './ResetSettingButton';

const DEFAULT_EMPTY_SETTINGS: LinePlotSettings = {};

type SettingToggleRowProps = {
  clearSetting?: ClearSettingFnType;
  description?: string;
  /** Determines how label and description should be aligned with respect to one another */
  descriptionAlignment?: 'row' | 'column';
  disabled?: boolean;
  displayValue: any;
  emptySettings?: Record<string, any>;
  /** Shown in an info icon tooltip next to the label */
  info?: string;
  label: string;
  numOverrides?: SettingOverrideCounts;
  onCheckedChange: (newValue: any) => void;
  settingKey?: string;
  settingLevel?: SettingLevel;
  value?: boolean | undefined;
};

export const SettingToggleRow = (props: SettingToggleRowProps & {}) => {
  const {clearSetting, emptySettings, numOverrides, settingKey, settingLevel} =
    props;
  const clearSettingRows = useClearSettingRows({
    clearSetting,
    emptySettings,
    numOverrides,
    settingKey,
    settingLevel,
  });

  if (
    props.descriptionAlignment == null ||
    props.descriptionAlignment === 'row'
  ) {
    return (
      <SettingToggleRowAligned clearSettingRows={clearSettingRows} {...props} />
    );
  }

  return (
    <SettingToggleColumnAligned
      clearSettingRows={clearSettingRows}
      {...props}
    />
  );
};

const SettingToggleRowAligned = ({
  clearSetting,
  clearSettingRows,
  description,
  disabled,
  displayValue,
  emptySettings,
  info,
  label,
  onCheckedChange,
  settingKey,
  settingLevel,
  value,
}: SettingToggleRowProps & {
  clearSettingRows: ClearSettingRow[];
}) => {
  return (
    <div className={twMerge('flex flex-col')}>
      <div
        className={twMerge(
          description == null ? 'flex justify-between' : 'flex items-center'
        )}>
        <div
          className={twMerge(
            'flex items-start gap-8',
            description == null ? 'grow' : 'grow justify-between'
          )}>
          <Switch
            description={description}
            isDisabled={disabled}
            isSelected={Boolean(displayValue)}
            onChange={onCheckedChange}>
            {label}
            <SettingInfoTooltip info={info} label={label} />
          </Switch>
          <SettingToggleResetSettingButton
            clearSetting={clearSetting}
            emptySettings={emptySettings}
            settingKey={settingKey}
            settingLevel={settingLevel}
            value={value}
          />
        </div>
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

const SettingToggleColumnAligned = ({
  clearSetting,
  clearSettingRows,
  description,
  disabled,
  displayValue,
  emptySettings,
  info,
  label,
  onCheckedChange,
  settingKey,
  settingLevel,
  value,
}: SettingToggleRowProps & {
  clearSettingRows: ClearSettingRow[];
}) => {
  return (
    <div className={twMerge('flex flex-col')}>
      <div className={twMerge('flex items-start gap-8')}>
        <Switch
          description={description}
          isDisabled={disabled}
          isSelected={Boolean(displayValue)}
          onChange={onCheckedChange}>
          {label}
          <SettingInfoTooltip info={info} label={label} />
        </Switch>
        <SettingToggleResetSettingButton
          clearSetting={clearSetting}
          emptySettings={emptySettings}
          settingKey={settingKey}
          settingLevel={settingLevel}
          value={value}
        />
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

const SettingInfoTooltip = ({info, label}: {info?: string; label: string}) => {
  const id = useId();
  if (info == null) {
    return null;
  }
  // Sits inside the Switch's <label>; without preventDefault a click on the
  // icon would also toggle the switch.
  return (
    <span
      className="ml-4 inline-flex align-middle"
      onClick={e => e.preventDefault()}>
      <InfoTooltip id={id} aria-label={`About ${label}`} size="sm">
        {info}
      </InfoTooltip>
    </span>
  );
};

const SettingToggleResetSettingButton = ({
  clearSetting,
  emptySettings = DEFAULT_EMPTY_SETTINGS,
  settingKey,
  settingLevel,
  value,
}: {
  clearSetting?: ClearSettingFnType;
  emptySettings?: LinePlotSettings;
  settingKey?: string;
  settingLevel?: SettingLevel;
  value?: boolean | undefined;
}) => {
  if (settingKey == null || settingLevel == null) {
    return null;
  }

  return (
    <ResetSettingButton
      isEnabled={calculateIsSettingEnabled(settingKey, {
        [settingKey]: value,
      })}
      onButtonClick={() => {
        clearSetting?.(settingLevel, settingKey, emptySettings);
      }}
      settingLevel={settingLevel}
    />
  );
};
